import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  fetchServices as fetchServicesAPI,
  upsertService as upsertServiceAPI,
  deleteService as deleteServiceAPI,
} from '../utils/servicesAPI.js';
import {
  fetchPackages as fetchPackagesAPI,
  upsertPackage as upsertPackageAPI,
  deletePackage as deletePackageAPI,
} from '../utils/packagesAPI.js';
import {
  getUnits, getUsers, getProfessionals, getPerformance, getServices, getPackages,
  getUnitPerformance, saveUnits, saveUsers, upsertPerformance,
  saveServices, savePackages, upsertUnitPerformance,
  getDailyRevenue, getUnitMetas, getHolidays, getUnitConfig,
  upsertDailyRevenue, upsertUnitMeta, upsertUnitConfig,
  getProfChallenges, upsertProfChallenge, deleteProfChallenge,
  getWeeklyDuels, upsertWeeklyDuel,
  loadFromCloud, pushToCloud, initStorage, flushPendingUploads,
  gravarProfissionalNovo, gravarEdicaoProfissional, gravarExclusaoProfissional, gravarDiasFechados,
  bloquearDia, desbloquearDia,
} from '../utils/storage.js';
import { loadPhotos } from '../utils/supabase.js';
import { SEED_TRAVADO, CRM_SEM_RECARGA, CADASTRO_SEED_TRAVADO } from '../config/featureFlags.js';
import { pollingGlobalPausado, ouvirRotaCrm } from '../utils/crmRotaAtiva.js';
import {
  listClients, upsertClient as upsertClientCloud, deleteClient as deleteClientCloud,
  mergeClients as mergeClientsCloud,
} from '../utils/clientsStorage.js';
import {
  listAppointments, upsertAppointment as upsertAppointmentCloud,
  deleteAppointment as deleteAppointmentCloud,
} from '../utils/appointmentsStorage.js';
import {
  listCaixas, upsertCaixa as upsertCaixaCloud, deleteCaixa as deleteCaixaCloud,
  listComandas, upsertComanda as upsertComandaCloud,
  listCashMovements, upsertCashMovement as upsertCashMovementCloud,
} from '../utils/caixaComandaStorage.js';
import {
  listNotasFiscais, upsertNotaFiscal as upsertNotaFiscalCloud,
  deleteNotaFiscal as deleteNotaFiscalCloud,
} from '../utils/notasFiscaisStorage.js';
import {
  listProducts, upsertProduct as upsertProductCloud, deleteProduct as deleteProductCloud,
  listSuppliers, upsertSupplier as upsertSupplierCloud,
  listStockMovements, upsertStockMovement as upsertStockMovementCloud,
  listPurchaseOrders, upsertPurchaseOrder as upsertPurchaseOrderCloud,
  listInventoryCounts, upsertInventoryCount as upsertInventoryCountCloud,
} from '../utils/estoqueStorage.js';
import { useAuth } from './AuthContext.jsx';
import { escopoListaClientes } from '../utils/papelClientes.js';
import {
  listPackageCredits, upsertPackageCredit as upsertPackageCreditCloud,
  createCreditsBatch, consumePackageCredit as consumePackageCreditCloud,
  refundPackageCredit as refundPackageCreditCloud,
  deactivateCreditsBySource as deactivateCreditsBySourceCloud,
  buildCreditsFromPackageSale,
} from '../utils/packageCreditsStorage.js';

// Merge cloud photos (tabela professionals.photo_url) no array de professionals.
// Fotos só ficam em fk_professionals.json se foram uploadadas como dataURL — quando
// foram pro Supabase Storage, a URL fica na tabela `professionals` (photo_url).
// Sem esse merge, Duels/Ranking/etc não veem foto nenhuma.
function mergeProfessionalsWithCloudPhotos(pros, photoMap) {
  if (!photoMap || Object.keys(photoMap).length === 0) return pros;
  return pros.map(p => photoMap[p.id] ? { ...p, photo: photoMap[p.id] } : p);
}

// ─── O que pedir de cash_movements (06/10/2026 — Financeiro Fase 0) ──────
// O /api/db agora restringe cash_movements (api/_lib/fin-acesso.js):
//   - operador financeiro → tudo. 06/10/2026: LISTA FECHADA no servidor
//     (master + contas liberadas, api/_lib/fin-operadores.js) — o cliente não
//     tem a lista: conta admin fora dos cargos abaixo PEDE tudo e o servidor
//     responde 403 se ela não for operadora (vira 'negado', sem re-tentar);
//   - Recepcionista/Gerente → só os movimentos de CAIXA da(s) unidade(s) dela
//     (é só isso que as abas Caixas usam: sangria/suprimento por caixaId);
//   - demais (barbeiro, sócio, marketing, admin fora da lista) → nada (403).
// Este espelho só decide O QUE PEDIR (não pedir nada pra quem com certeza não
// tem acesso; pedir o recorte certo pra recepção). Quem decide é o servidor.
const CARGOS_SEM_FINANCEIRO = new Set(['Gerente', 'Recepcionista', 'Sócio', 'Marketing']);
// Quem PEDE o recorte do caixa aqui. A Gerente também tem esse recorte no
// servidor, mas nenhuma tela dela usa movimentos (o Financeiro é bloqueado pra
// ela no App.jsx) — então nem pede (06/10/2026, revisão).
const CARGOS_CAIXA = new Set(['Recepcionista']);
function cashMovementsScope(user, isMaster, professionals) {
  if (!user) return { modo: 'nenhum', unitIds: [] };
  if (isMaster) return { modo: 'total', unitIds: [] };
  // Conta com cadastro ligado e cadastro ainda não carregado (boot de aparelho
  // novo): sem o cargo, a recepção com conta 'admin' cairia em 'total' e
  // levaria 403 → lista vazia até o cadastro chegar. Espera o cadastro.
  if (user.professionalId && !(professionals || []).length) return { modo: 'aguardando', unitIds: [] };
  const prof = user.professionalId ? (professionals || []).find(p => p.id === user.professionalId) : null;
  const cargo = prof?.role || null;
  if (CARGOS_CAIXA.has(cargo)) {
    const lista = cargo === 'Gerente' && Array.isArray(prof.gerenteUnitIds) && prof.gerenteUnitIds.length
      ? prof.gerenteUnitIds : [prof.unitId];
    const unitIds = [...new Set(lista.filter(u => u && u !== 'geral'))];
    const ativo = prof.active !== false && !prof.deletedAt;
    return ativo && unitIds.length ? { modo: 'caixa', unitIds } : { modo: 'nenhum', unitIds: [] };
  }
  if (CARGOS_SEM_FINANCEIRO.has(cargo)) return { modo: 'nenhum', unitIds: [] };
  if (user.role === 'admin') return { modo: 'total', unitIds: [] };
  return { modo: 'nenhum', unitIds: [] };
}
// 403 do proxy = "sem acesso" (não é falha de rede: não re-tenta, não vira
// aviso de "não carregou"). restFetchAll lança "HTTP 403 em …".
const isSemAcesso = (err) => /\bHTTP 403\b/.test(String(err?.message || ''));

// Janela da carga de caixas/comandas/movimentos: 90 dias atrás → hoje.
// Janela ESTENDIDA pra cash_movements: contas a pagar com vencimento futuro
// têm date=vencimento (não today), então a janela today-90→today perdia elas.
// Estende pra +180 dias à frente — cobre boletos do próximo semestre. Caixas
// e comandas continuam com janela curta (são sempre passado).
function janelaCaixaComanda() {
  const today = new Date();
  const start = new Date(today); start.setDate(today.getDate() - 90);
  const future = new Date(today); future.setDate(today.getDate() + 180);
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  return { startStr: fmt(start), endStr: fmt(today), mvEnd: fmt(future) };
}

// Movimentos conforme o escopo. null = escopo indefinido (não mexe no estado).
// 403 → { acesso:'negado', rows:[] }; outra falha → LANÇA (REGRA LINEAR).
async function carregarMovimentos(scope, startDate, endDate) {
  if (!scope || scope.modo === 'aguardando') return null;
  if (scope.modo === 'nenhum') return { acesso: 'negado', rows: [] };
  try {
    if (scope.modo === 'total') {
      return { acesso: 'total', rows: await listCashMovements({ startDate, endDate }) };
    }
    // Recorte do caixa: um pedido por unidade (o servidor exige unit_id=eq.<sua>)
    const partes = await Promise.all(scope.unitIds.map(unitId =>
      listCashMovements({ unitId, somenteCaixa: true, startDate, endDate })));
    const rows = partes.flat().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    return { acesso: 'caixa', rows };
  } catch (err) {
    if (isSemAcesso(err)) return { acesso: 'negado', rows: [] };
    throw err;
  }
}

// Cache em escopo de módulo do photoMap (tabela professionals.photo_url do Supabase).
// Carregado no mount, usado por TODOS os setProfessionals subsequentes pra que
// as fotos não sumam após auto-refresh (que re-lê do localStorage onde fotos não estão).
let CACHED_PHOTO_MAP = null;

const DataContext = createContext(null);

// Escopo das listas do balcão (ver "Quem recebe as listas do balcão" no
// provider): sem sessão ou sem acesso → a lista em memória é limpa.
const limparLista = (escopo) => escopo === 'nao' || escopo === 'nenhum';

// Run initStorage synchronously BEFORE any component renders
// This ensures localStorage is seeded/migrated before useState reads it
const _migrated = initStorage();

export function DataProvider({ children }) {
  const [units, setUnits] = useState(() => getUnits());
  const [users, setUsers] = useState(() => getUsers());
  const [professionals, setProfessionals] = useState(() => getProfessionals());
  const [performance, setPerformance] = useState(() => getPerformance());
  const [services, setServices] = useState(() => getServices());
  const [packages, setPackages] = useState(() => getPackages());
  const [unitPerformance, setUnitPerformance] = useState(() => getUnitPerformance());
  const [dailyRevenue, setDailyRevenue] = useState(() => getDailyRevenue());
  const [unitMetas, setUnitMetas] = useState(() => getUnitMetas());
  const [holidays, setHolidays] = useState(() => getHolidays());
  const [unitConfig, setUnitConfig] = useState(() => getUnitConfig());
  const [profChallenges, setProfChallenges] = useState(() => getProfChallenges());
  const [weeklyDuels, setWeeklyDuels] = useState(() => getWeeklyDuels());
  // Clientes (Supabase async — começa vazio, carrega no mount)
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  // Agenda (Fase 2 — Supabase async)
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoaded, setAppointmentsLoaded] = useState(false);
  // Caixa + Comandas + Movimentações (Fase 3a — Supabase async)
  const [caixas, setCaixas] = useState([]);
  const [comandas, setComandas] = useState([]);
  const [cashMovements, setCashMovements] = useState([]);
  const [caixasLoaded, setCaixasLoaded] = useState(false);
  // Notas Fiscais (Fase 3c — Supabase async)
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [notasFiscaisLoaded, setNotasFiscaisLoaded] = useState(false);
  // Estoque (Fase 4 — Supabase async)
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [inventoryCounts, setInventoryCounts] = useState([]);
  const [estoqueLoaded, setEstoqueLoaded] = useState(false);
  // Pacotes — créditos por cliente (Fase 5 — Supabase async)
  const [packageCredits, setPackageCredits] = useState([]);
  const [packageCreditsLoaded, setPackageCreditsLoaded] = useState(false);

  const [cloudLoaded, setCloudLoaded] = useState(false);
  // Tick incrementado após cada loadFromCloud bem-sucedido (boot + auto-refresh).
  // Outros contexts (AvecSync) escutam pra re-ler localStorage que foi sobrescrito
  // pelo cloud — caso contrário caches em React state ficam stale entre reloads.
  const [cloudReloadTick, setCloudReloadTick] = useState(0);

  // Services agora vêm da tabela Postgres via /api/services-list (v2 migration).
  // O cloud sync legado de fk_services.json continua rodando como fallback
  // durante a transição, mas o source-of-truth é o banco. Esse effect roda no
  // mount e quando refreshAll é chamado — sempre puxa fresco da API.
  const refreshServicesFromAPI = useCallback(async () => {
    try {
      const fresh = await fetchServicesAPI();
      setServices(fresh);
    } catch (e) {
      console.warn('[services] fallback pra cache local —', e?.message);
    }
  }, []);

  useEffect(() => { refreshServicesFromAPI(); }, [refreshServicesFromAPI]);

  // Packages — mesma estratégia do services. Migrado pra Postgres no Pacote C
  // (commit 8180d289+1). Antes: localStorage + cloudSave de fk_packages.json
  // → vulnerável a race conditions multi-device. Agora: API/REST sobre tabela
  // com ACID. Tombstones via deleted_at no DB.
  const refreshPackagesFromAPI = useCallback(async () => {
    try {
      const fresh = await fetchPackagesAPI();
      setPackages(fresh);
    } catch (e) {
      console.warn('[packages] fallback pra cache local —', e?.message);
    }
  }, []);

  useEffect(() => { refreshPackagesFromAPI(); }, [refreshPackagesFromAPI]);

  // On mount: ALWAYS load from cloud first, then push if needed.
  //
  // Resiliência: setCloudLoaded(true) MUST sempre rodar, mesmo se cloud
  // estiver inacessível ou alguma chave tiver dado corrompido. Senão, a
  // tela de login trava em "Sincronizando..." pra sempre.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Timeout duro: se o cloud não responder em 12s, segue offline
        // ao invés de travar a tela de login pra sempre.
        const cloudPromise = loadFromCloud().catch(() => false);
        const timeoutPromise = new Promise(resolve => setTimeout(() => resolve('timeout'), 12000));
        const result = await Promise.race([cloudPromise, timeoutPromise]);
        const hasCloud = result === 'timeout' ? false : result;
        if (result === 'timeout') console.warn('[boot] cloud sync timeout — seguindo offline');
        if (cancelled) return;
        if (hasCloud) {
          // Cada setX é wrapped — uma chave corrompida não bloqueia o resto
          const safeSet = (setter, getter, label) => {
            try { setter(getter()); }
            catch (e) { console.warn(`[boot] falha em ${label}:`, e?.message); }
          };
          safeSet(setUnits, getUnits, 'units');
          safeSet(setUsers, getUsers, 'users');
          safeSet(setProfessionals, getProfessionals, 'professionals');
          safeSet(setPerformance, getPerformance, 'performance');
          // ⚠️ packages NÃO entra aqui: o catálogo vive no POSTGRES
          // (refreshPackagesFromAPI). Repor do localStorage aqui sobrescrevia
          // a lista fresca com a cópia pré-migração — pacotes importados do
          // Avec "sumiam" da tela em segundos (dado intacto no banco). 16/07.
          safeSet(setUnitPerformance, getUnitPerformance, 'unitPerformance');
          safeSet(setDailyRevenue, getDailyRevenue, 'dailyRevenue');
          safeSet(setUnitMetas, getUnitMetas, 'unitMetas');
          safeSet(setHolidays, getHolidays, 'holidays');
          safeSet(setUnitConfig, getUnitConfig, 'unitConfig');
          safeSet(setProfChallenges, getProfChallenges, 'profChallenges');
          safeSet(setWeeklyDuels, getWeeklyDuels, 'weeklyDuels');
        }
        // SEED_TRAVADO (23/10/2026): não empurra mais a cópia local inteira
        // quando a migração rodou e o cofre não respondeu (num aparelho com o
        // armazenamento vazio essa cópia era o seed). O cofre é a fonte.
        if (!SEED_TRAVADO && _migrated && !hasCloud) {
          try { pushToCloud(); } catch {}
        }
        try {
          const photosTimeout = new Promise(resolve => setTimeout(() => resolve(null), 8000));
          const photoMap = await Promise.race([loadPhotos(), photosTimeout]);
          if (!cancelled && photoMap) {
            CACHED_PHOTO_MAP = photoMap;
            setProfessionals(prev => mergeProfessionalsWithCloudPhotos(prev, CACHED_PHOTO_MAP));
          }
        } catch { /* fotos são best-effort */ }
      } catch (e) {
        console.error('[boot] erro no fluxo de sync inicial:', e);
      } finally {
        // SEMPRE libera a UI — mesmo offline, mesmo com dado corrompido.
        if (!cancelled) {
          setCloudLoaded(true);
          setCloudReloadTick(t => t + 1);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Recarrega o cache de fotos periodicamente (5min) pra capturar uploads
  // feitos em outros dispositivos.
  useEffect(() => {
    if (!cloudLoaded) return;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      loadPhotos().then(map => {
        if (!map) return;
        CACHED_PHOTO_MAP = map;
        setProfessionals(prev => mergeProfessionalsWithCloudPhotos(prev, CACHED_PHOTO_MAP));
      }).catch(() => {});
    }, 5 * 60_000);
    return () => clearInterval(interval);
  }, [cloudLoaded]);

  // Login fresco: o boot acima pode ter rodado antes da sessão existir, e
  // loadPhotos agora passa pelo proxy autenticado (401 sem sessão). Ao logar,
  // o AuthContext dispara 'fk-login' e a gente re-busca as fotos na hora.
  useEffect(() => {
    const onLogin = () => {
      loadPhotos().then(map => {
        if (!map) return;
        CACHED_PHOTO_MAP = map;
        setProfessionals(prev => mergeProfessionalsWithCloudPhotos(prev, CACHED_PHOTO_MAP));
      }).catch(() => {});
    };
    window.addEventListener('fk-login', onLogin);
    return () => window.removeEventListener('fk-login', onLogin);
  }, []);

  const refreshAll = useCallback(() => {
    setUnits(getUnits());
    setUsers(getUsers());
    setProfessionals(mergeProfessionalsWithCloudPhotos(getProfessionals(), CACHED_PHOTO_MAP));
    setPerformance(getPerformance());
    refreshServicesFromAPI(); // tabela Postgres — não do localStorage
    // packages também é Postgres: repor do localStorage aqui fazia o
    // auto-refresh de 60s "sumir" com os pacotes importados do Avec (16/07)
    refreshPackagesFromAPI();
    setUnitPerformance(getUnitPerformance());
    setDailyRevenue(getDailyRevenue());
    setUnitMetas(getUnitMetas());
    setHolidays(getHolidays());
    setUnitConfig(getUnitConfig());
    setProfChallenges(getProfChallenges());
    setWeeklyDuels(getWeeklyDuels());
  }, []);

  // Força um re-pull do cloud e atualiza todos os states.
  // Usado pelo botão "Atualizar" no header — garante que o usuário receba dados
  // criados em outro dispositivo (ex: admin definiu desafio, profissional puxa).
  //
  // ANTES de pull: aguarda uploads pendentes terminarem (até 3s).
  // Isso evita perda de mudanças recentes do usuário se o auto-refresh
  // disparar logo após uma edição/exclusão.
  const refreshFromCloud = useCallback(async () => {
    await flushPendingUploads(3000);
    const hasCloud = await loadFromCloud();
    refreshAll();
    setCloudReloadTick(t => t + 1);
    return hasCloud;
  }, [refreshAll]);

  // Login fresco: antes do login o cofre (/api/data) não responde — sem sessão
  // nada é lido nem gravado. Ao entrar, puxa tudo na hora em vez de esperar o
  // próximo ciclo de 60s.
  useEffect(() => {
    const onLogin = () => { refreshFromCloud().catch(() => {}); };
    window.addEventListener('fk-login', onLogin);
    return () => window.removeEventListener('fk-login', onLogin);
  }, [refreshFromCloud]);

  // Auto-refresh: a cada 60s, puxa do cloud silenciosamente.
  // Se o usuário tá com o app aberto e admin criou/editou algo em outro device,
  // até 60s depois o usuário recebe a atualização sem precisar clicar em nada.
  // Só roda quando a aba está visível (economiza bateria/dados).
  const lastCloudRefreshRef = useRef(0);
  useEffect(() => {
    if (!cloudLoaded) return;
    let interval;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        // 27/10/2026 (CRM_SEM_RECARGA): com o CRM aberto (não usa fk_*) pula a rodada; ao sair, relê
        if (pollingGlobalPausado({ ligado: CRM_SEM_RECARGA })) return;
        if (document.visibilityState === 'visible') {
          lastCloudRefreshRef.current = Date.now();
          refreshFromCloud().catch(() => {});
        }
      }, 60_000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        // Refresh ao voltar pra aba SÓ se passou >2min desde o último
        // (evita re-fetch toda vez que user troca de aba rapidamente).
        const elapsed = Date.now() - lastCloudRefreshRef.current;
        if (elapsed > 2 * 60_000) {
          lastCloudRefreshRef.current = Date.now();
          refreshFromCloud().catch(() => {});
        }
        start();
      } else {
        // ⚠️ Ao sair da aba: força upload pendente AGORA (fetch keepalive sobrevive
        // ao fechamento da tab). Sem isso, mudanças locais não sobem se o user
        // fecha o browser antes do cloudSave async completar.
        stop();
        flushPendingUploads(2000).catch(() => {});
      }
    };
    // beforeunload/pagehide: última chance de upload antes do tab morrer
    const onBeforeUnload = () => {
      flushPendingUploads(1000).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);
    // saiu do CRM: a mesma regra da volta à aba (relê só se passou de 2 min)
    const pararOuvirCrm = CRM_SEM_RECARGA ? ouvirRotaCrm((ativa) => { if (!ativa && document.visibilityState === 'visible') onVis(); }) : null;
    return () => {
      stop();
      pararOuvirCrm?.();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
    };
  }, [cloudLoaded, refreshFromCloud]);

  const addUnit = useCallback((unit) => {
    const now = new Date().toISOString();
    const stamped = { ...unit, updatedAt: now, createdAt: unit.createdAt || now };
    const updated = [...getUnits(), stamped];
    saveUnits(updated);
    setUnits(updated);
  }, []);

  const updateUnit = useCallback((unit) => {
    const all = getUnits();
    const now = new Date().toISOString();
    const updated = all.map(u => u.id === unit.id ? { ...u, ...unit, updatedAt: now } : u);
    saveUnits(updated);
    setUnits(updated);
  }, []);

  // Soft-delete com tombstone — não some do array (pra sync respeitar).
  // visibleUnits filtra deletedAt na exposição. Mantém active=false também
  // pra UI/lookups legacy continuarem funcionando.
  const deleteUnit = useCallback((unitId) => {
    const all = getUnits();
    const now = new Date().toISOString();
    const updated = all.map(u => u.id === unitId
      ? { ...u, deletedAt: now, updatedAt: now, active: false }
      : u
    );
    saveUnits(updated);
    setUnits(updated);
  }, []);

  // As gravações do cadastro moram em utils/storage.js (gravarProfissionalNovo,
  // gravarEdicaoProfissional, gravarExclusaoProfissional): com
  // CADASTRO_SEED_TRAVADO (28/10/2026) elas leem a cópia CRUA (sem o alias do
  // seed que a leitura completa) e a tela relê a lista com ele.
  const mostrarCadastroGravado = (updated) => setProfessionals(
    mergeProfessionalsWithCloudPhotos(CADASTRO_SEED_TRAVADO ? getProfessionals() : updated, CACHED_PHOTO_MAP));

  const addProfessional = useCallback((professional) => {
    mostrarCadastroGravado(gravarProfissionalNovo(professional));
  }, []);

  // opts.silent: NÃO carimba updatedAt (descoberta de CPF via Avec);
  // opts.campos: só os campos que a pessoa mudou (o servidor aplica só eles).
  // Detalhes em utils/storage.js (gravarEdicaoProfissional).
  const updateProfessional = useCallback((professional, opts = {}) => {
    mostrarCadastroGravado(gravarEdicaoProfissional(professional, opts));
  }, []);

  // Soft-delete com tombstone — sync respeita cross-device
  const deleteProfessional = useCallback((professionalId) => {
    mostrarCadastroGravado(gravarExclusaoProfissional(professionalId));
  }, []);

  // Helper interno: pega session token do storage
  const getSessionToken = () => {
    try {
      const s = sessionStorage.getItem('fk_session');
      return s ? JSON.parse(s)?.sessionToken : null;
    } catch { return null; }
  };

  // Recarrega users da API (server-side, com hash). Chamado após criar/editar.
  const refreshUsersFromAPI = useCallback(async () => {
    const sessionToken = getSessionToken();
    if (!sessionToken) return;
    try {
      const r = await fetch('/api/auth-list-users', {
        headers: { 'x-session-token': sessionToken },
      });
      if (r.ok) {
        const data = await r.json();
        const apiUsers = data.users || [];
        setUsers(apiUsers);
        // Mantém localStorage sincronizado pra read offline (sem password)
        try { saveUsers(apiUsers); } catch {}
      }
    } catch {}
  }, []);

  // Auto-carrega users do server após cloud load (quando admin logado).
  useEffect(() => {
    if (cloudLoaded) refreshUsersFromAPI();
  }, [cloudLoaded, refreshUsersFromAPI]);

  // Traduz erros 401/sessão inválida em mensagem clara pra UI
  const translateAuthError = (status, serverError) => {
    if (status === 401 || serverError === 'Unauthorized') {
      return 'Sua sessão está desatualizada. Faça LOGOUT e LOGIN de novo, depois tente outra vez.';
    }
    return serverError || `Erro ${status}`;
  };

  // addUser agora cria via /api/auth-create-user (hash forte, audit log).
  // Retorna { ok, error? } — caller deve await e tratar erro.
  const addUser = useCallback(async (user) => {
    const sessionToken = getSessionToken();
    if (!sessionToken) {
      return { ok: false, error: 'Sua sessão está desatualizada. Faça LOGOUT e LOGIN de novo, depois tente outra vez.' };
    }
    try {
      const r = await fetch('/api/auth-create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken,
        },
        body: JSON.stringify({
          id: user.id,
          name: user.name,
          email: user.email,
          password: user.password,
          role: user.role,
          professionalId: user.professionalId || null,
          mustChangePassword: !!user.mustChangePassword,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: translateAuthError(r.status, data?.error) };
      await refreshUsersFromAPI();
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: String(err?.message || err) };
    }
  }, [refreshUsersFromAPI]);

  // updateUser agora chama /api/auth-update-user. Se houver `password` no
  // payload, chama /api/auth-admin-set-password (admin define direto).
  const updateUser = useCallback(async (user) => {
    const sessionToken = getSessionToken();
    if (!sessionToken) {
      return { ok: false, error: 'Sua sessão está desatualizada. Faça LOGOUT e LOGIN de novo, depois tente outra vez.' };
    }
    try {
      const { id: userId, password, ...rest } = user;
      // 1) Atualiza campos não-senha
      const fieldsToUpdate = {};
      ['name', 'email', 'role', 'professionalId', 'active', 'mustChangePassword'].forEach(k => {
        if (k in rest) fieldsToUpdate[k] = rest[k];
      });
      if (Object.keys(fieldsToUpdate).length > 0) {
        const r = await fetch('/api/auth-update-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': sessionToken,
          },
          body: JSON.stringify({ userId, updates: fieldsToUpdate }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) return { ok: false, error: translateAuthError(r.status, data?.error) };
      }
      // 2) Se passou nova senha, seta via admin-set-password (sem currentPassword)
      if (password && String(password).trim()) {
        const r2 = await fetch('/api/auth-admin-set-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': sessionToken,
          },
          body: JSON.stringify({ userId, newPassword: password }),
        });
        const data2 = await r2.json().catch(() => ({}));
        if (!r2.ok) return { ok: false, error: translateAuthError(r2.status, data2?.error) };
      }
      await refreshUsersFromAPI();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: String(err?.message || err) };
    }
  }, [refreshUsersFromAPI]);

  const savePerf = useCallback((record) => {
    upsertPerformance(record);
    setPerformance(getPerformance());
  }, []);

  const saveUnitPerf = useCallback((record) => {
    upsertUnitPerformance(record);
    setUnitPerformance(getUnitPerformance());
  }, []);

  // Services agora vivem na tabela Postgres (via /api/services-*) — não usa mais
  // localStorage + cloud sync de fk_services.json. Resolve o bug de "deleções
  // voltando" quando devices com cache antigo empurram o array inteiro de volta.
  const addService = useCallback(async (service) => {
    const saved = await upsertServiceAPI(service);
    setServices(prev => {
      const idx = prev.findIndex(s => s.id === saved.id);
      if (idx >= 0) { const n = prev.slice(); n[idx] = saved; return n; }
      return [...prev, saved];
    });
    return saved;
  }, []);

  const updateService = useCallback(async (service) => {
    const saved = await upsertServiceAPI(service);
    setServices(prev => prev.map(s => s.id === saved.id ? saved : s));
    return saved;
  }, []);

  // Remove via API → sai do banco. Snapshots em comandas/apts preservam name+price,
  // então é seguro pra histórico — só quebra `prof.serviceIds[]` e `pkg.items[].serviceId`
  // que referenciam por id (a UI já mostra "(serviço removido)" quando não acha).
  const deleteService = useCallback(async (serviceId) => {
    if (!serviceId) return;
    await deleteServiceAPI(serviceId);
    setServices(prev => prev.filter(s => s.id !== serviceId));
  }, []);

  // 📦 Pacotes via API/Postgres (Pacote C). Race-free + ACID + tombstones no DB.
  const addPackage = useCallback(async (pkg) => {
    const saved = await upsertPackageAPI(pkg);
    setPackages(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      if (idx >= 0) { const n = prev.slice(); n[idx] = saved; return n; }
      return [...prev, saved];
    });
    return saved;
  }, []);

  const updatePackage = useCallback(async (pkg) => {
    const saved = await upsertPackageAPI(pkg);
    setPackages(prev => prev.map(p => p.id === saved.id ? saved : p));
    return saved;
  }, []);

  // Soft-delete via endpoint — backend marca deleted_at no DB.
  // Lista local refresha automaticamente via refresh ou removendo aqui.
  const deletePackage = useCallback(async (packageId) => {
    await deletePackageAPI(packageId);
    setPackages(prev => prev.map(p => p.id === packageId
      ? { ...p, deletedAt: new Date().toISOString(), active: false }
      : p
    ));
  }, []);

  const saveDailyRevenueRecord = useCallback((record) => {
    upsertDailyRevenue(record);
    setDailyRevenue(getDailyRevenue());
  }, []);

  const saveUnitMetaRecord = useCallback((record) => {
    upsertUnitMeta(record);
    setUnitMetas(getUnitMetas());
  }, []);

  const saveUnitConfigRecord = useCallback((record) => {
    upsertUnitConfig(record);
    setUnitConfig(getUnitConfig());
  }, []);

  // Retorna false (nada gravado) quando CADASTRO_SEED_TRAVADO e os dias fechados
  // ainda não foram conferidos com o cofre nesta sessão (utils/storage.js,
  // gravarDiasFechados); a tela avisa e a pessoa tenta de novo.
  const saveHolidaysList = useCallback((list) => {
    if (!gravarDiasFechados(list)) return false;
    setHolidays(getHolidays());
    return true;
  }, []);

  // Bloquear/Desbloquear dia (Metas). CADASTRO_SEED_TRAVADO (revisão de
  // 28/10/2026): uma operação sobre a cópia aplicada do aparelho (utils/storage.js,
  // bloquearDia/desbloquearDia), nunca a lista da tela, que fica [] num boot que
  // passa dos 12 s. Desligada: a lista da tela, exatamente como antes.
  const bloquearDiaFechado = useCallback((dia, listaDaTela) => {
    const ok = CADASTRO_SEED_TRAVADO ? bloquearDia(dia) : gravarDiasFechados([...(listaDaTela || []), dia]);
    if (ok) setHolidays(getHolidays());
    return ok;
  }, []);
  const desbloquearDiaFechado = useCallback((id, listaDaTela) => {
    const ok = CADASTRO_SEED_TRAVADO ? desbloquearDia(id) : gravarDiasFechados((listaDaTela || []).filter(h => h.id !== id));
    if (ok) setHolidays(getHolidays());
    return ok;
  }, []);

  const saveProfChallenge = useCallback((record) => {
    upsertProfChallenge(record);
    setProfChallenges(getProfChallenges());
  }, []);

  const removeProfChallenge = useCallback((id) => {
    deleteProfChallenge(id);
    setProfChallenges(getProfChallenges());
  }, []);

  const saveWeeklyDuel = useCallback((weekKey, data) => {
    upsertWeeklyDuel(weekKey, data);
    setWeeklyDuels(getWeeklyDuels());
  }, []);

  const getProfessionalPerf = useCallback((professionalId, year, month) => {
    return performance.find(r => r.professionalId === professionalId && r.year === year && r.month === month) || null;
  }, [performance]);

  const getUnitPerf = useCallback((unitId, year, month) => {
    return performance.filter(r => r.unitId === unitId && r.year === year && r.month === month);
  }, [performance]);

  const getUnitPerfRecord = useCallback((unitId, year, month) => {
    return unitPerformance.find(r => r.unitId === unitId && r.year === year && r.month === month) || null;
  }, [unitPerformance]);

  const getPeriodPerf = useCallback((year, month) => {
    return performance.filter(r => r.year === year && r.month === month);
  }, [performance]);

  // ─── Quem recebe as listas do balcão (16/10/2026 — segurança) ─────────
  // Clientes (≈21 mil, com CPF/telefone/nascimento), comandas (nome e telefone
  // do cliente) e agenda só vêm para a gestão (conta 'admin') e para o balcão
  // (Recepcionista/Gerente) — src/utils/papelClientes.js, espelho da regra R2
  // do /api/db (api/_lib/db-papeis.js). Para os demais (barbeiro…) as listas
  // ficam vazias, sem erro: nenhuma tela dele usa (inventário e teste em
  // tests/db-papeis.test.mjs). Antes o DataContext baixava tudo em TODO login.
  //   'aguardando' (sessão/cadastro resolvendo) → não pede nem mexe;
  //   'nenhum' (sem sessão) → não pede e limpa; 'nao' → limpa; 'sim' → pede.
  // A carga roda quando o escopo muda (boot, login, troca de conta) — antes
  // só no mount, e um login sem recarregar a página ficava sem as listas.
  const authCtx = useAuth();
  const authUser = authCtx?.user || null;
  const authLoading = !!authCtx?.loading;
  const authIsMaster = !!authCtx?.isMaster;
  const listaEscopo = useMemo(
    () => escopoListaClientes({ user: authUser, authLoading, professionals }),
    [authUser, authLoading, professionals],
  );
  const listaKey = `${authUser?.id || ''}|${listaEscopo}`;
  const listaEscopoRef = useRef(listaEscopo);
  listaEscopoRef.current = listaEscopo;

  // ─── Clientes ──────────────────────────────────────────────────────────
  // Carrega quando o escopo libera (ver acima) + refresher manual.
  // Carrega via proxy (paginado). Um blip no proxy pode devolver [] no boot e,
  // sem clientes, a BUSCA DA AGENDA fica vazia a sessão inteira. Como a rede tem
  // ~20k clientes, [] = quase sempre um blip transitório (não "sem clientes") →
  // tenta de novo algumas vezes com backoff até vir algo.
  const cliReqRef = useRef(0);        // só o pedido MAIS NOVO de clientes aplica
  const refreshClients = useCallback(async () => {
    const escopo = listaEscopoRef.current;
    if (escopo !== 'sim') {
      if (limparLista(escopo)) {
        ++cliReqRef.current;
        setClients([]);
        setClientsLoaded(escopo === 'nao');
      }
      return;
    }
    const seq = ++cliReqRef.current;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const rows = await listClients();
        if (seq !== cliReqRef.current) return; // escopo mudou (logout/outra conta)
        if (Array.isArray(rows) && rows.length > 0) {
          setClients(rows);
          setClientsLoaded(true);
          return;
        }
      } catch (err) {
        if (seq !== cliReqRef.current) return;
        console.warn(`[clients] tentativa ${attempt + 1} falhou:`, err?.message);
      }
      if (attempt < 3) await new Promise(r => setTimeout(r, 700 * (attempt + 1)));
    }
    if (seq === cliReqRef.current) setClientsLoaded(true); // desistiu após as tentativas — mantém o estado atual
  }, []);

  useEffect(() => { refreshClients(); }, [listaKey, refreshClients]);

  const saveClient = useCallback(async (record) => {
    const saved = await upsertClientCloud(record);
    setClients(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = saved;
        return next;
      }
      return [...prev, saved].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
    return saved;
  }, []);

  const removeClient = useCallback(async (id) => {
    await deleteClientCloud(id);
    setClients(prev => prev.filter(c => c.id !== id));
  }, []);

  const mergeClients = useCallback(async (targetId, sourceId) => {
    const merged = await mergeClientsCloud(targetId, sourceId);
    setClients(prev => prev.filter(c => c.id !== sourceId).map(c => c.id === targetId ? merged : c));
    return merged;
  }, []);

  // ─── Agenda ────────────────────────────────────────────────────────────
  // Mesmo escopo dos clientes (ver "Quem recebe as listas do balcão").
  const aptReqRef = useRef(0);
  const refreshAppointments = useCallback(async () => {
    const escopo = listaEscopoRef.current;
    if (escopo !== 'sim') {
      if (limparLista(escopo)) {
        ++aptReqRef.current;
        setAppointments([]);
        setAppointmentsLoaded(escopo === 'nao');
      }
      return;
    }
    const seq = ++aptReqRef.current;
    // Janela razoável: 60 dias passados + 90 futuros (suficiente pra navegação)
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() - 60);
    const end = new Date(today); end.setDate(today.getDate() + 90);
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    // Retry com backoff (igual clientes). Em falha, NÃO zera os agendamentos
    // já carregados — evita o "pisca" (um 401/502 momentâneo sumia com tudo
    // da agenda e depois voltava). Só sobrescreve num fetch bem-sucedido
    // (lista vazia real = range sem agenda).
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const rows = await listAppointments({ startDate: fmt(start), endDate: fmt(end) });
        if (seq !== aptReqRef.current) return; // escopo mudou (logout/outra conta)
        setAppointments(rows);
        setAppointmentsLoaded(true);
        return;
      } catch (err) {
        if (seq !== aptReqRef.current) return;
        console.warn(`[appointments] tentativa ${attempt + 1} falhou:`, err?.message);
        if (attempt < 3) await new Promise(r => setTimeout(r, 700 * (attempt + 1)));
      }
    }
    // Todas as tentativas falharam: mantém os agendamentos atuais (não limpa).
    if (seq === aptReqRef.current) setAppointmentsLoaded(true);
  }, []);

  useEffect(() => { refreshAppointments(); }, [listaKey, refreshAppointments]);

  const saveAppointmentRecord = useCallback(async (record) => {
    try {
      const saved = await upsertAppointmentCloud(record);
      setAppointments(prev => {
        const idx = prev.findIndex(a => a.id === saved.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
        return [...prev, saved].sort((a, b) =>
          (a.date || '').localeCompare(b.date || '') || (a.startTime || '').localeCompare(b.startTime || '')
        );
      });
      return saved;
    } catch (err) {
      console.error('[saveAppointmentRecord] payload:', record, 'err:', err);
      alert(`Erro ao salvar agendamento:\n${err.message}`);
      throw err;
    }
  }, []);

  const removeAppointment = useCallback(async (id) => {
    try {
      await deleteAppointmentCloud(id);
      setAppointments(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('[removeAppointment] err:', err);
      alert(`Erro ao excluir agendamento:\n${err.message}`);
      throw err;
    }
  }, []);

  // ─── Caixas + Comandas + Movimentações ────────────────────────────────
  // Carrega em paralelo no mount. Janela: 90 dias atrás (caixas/comandas
  // antigos não precisam ficar em memória — query dedicada quando precisar).
  //
  // REGRA LINEAR (incidente 15/07/2026 — "comandas sumiram"): falha de carga
  // NUNCA zera o estado — mantém o que está na tela, tenta de novo com
  // backoff e expõe `caixaLoadError` pra UI avisar. Antes, um erro virava
  // console.warn + telas vazias, e o usuário lia como dado apagado.
  const [caixaLoadError, setCaixaLoadError] = useState(null);

  // 06/10/2026 (Financeiro Fase 0): cash_movements tem acesso por cargo no
  // servidor e NÃO entra mais no mesmo Promise.all "tudo ou nada" de
  // caixas/comandas — um 403 ali derrubava as comandas de quem não tem o
  // financeiro (barbeiro). Agora:
  //   - pede só o que o cargo pode ver (cashMovementsScope): nada / recorte
  //     do caixa da unidade / tudo;
  //   - 403 = SEM ACESSO: cashMovements = [] com cashMovementsAcesso='negado'
  //     (nunca "não há lançamentos" gravável), sem re-tentar nem avisar falha;
  //   - falha de rede segue a REGRA LINEAR (mantém o estado, avisa, re-tenta).
  // cashMovementsAcesso: 'total' | 'caixa' | 'negado' | null (ainda não carregou).
  const [cashMovementsAcesso, setCashMovementsAcesso] = useState(null);
  // (authCtx/authUser/authLoading/authIsMaster: lidos lá em cima, em "Quem
  // recebe as listas do balcão".)
  const cashScope = useMemo(
    () => (authLoading ? { modo: 'aguardando', unitIds: [] } : cashMovementsScope(authUser, authIsMaster, professionals)),
    [authLoading, authUser, authIsMaster, professionals],
  );
  // Chave estável: o professionals muda a cada auto-refresh, o escopo quase nunca.
  const cashScopeKey = `${authUser?.id || ''}|${cashScope.modo}|${cashScope.unitIds.join(',')}`;
  const cashScopeRef = useRef({ scope: cashScope, key: cashScopeKey });
  cashScopeRef.current = { scope: cashScope, key: cashScopeKey };
  const movReqRef = useRef(0);        // só o pedido MAIS NOVO de movimentos aplica
  const movKeyCarregadaRef = useRef(null);

  // Movimentos: carga PRÓPRIA (retry e aviso próprios). 06/10/2026 (revisão):
  // antes ficavam no mesmo Promise.all de caixas/comandas — o 503 do proxy
  // (cadastro ilegível numa instância fria, fail-closed) derrubava caixas e
  // comandas do balcão junto. Agora a falha dos movimentos só acende
  // cashMovementsLoadError; caixas/comandas carregam do mesmo jeito.
  //   force=false → não repete a chave já carregada (ou em curso).
  const [cashMovementsLoadError, setCashMovementsLoadError] = useState(null);
  const movKeyEmCursoRef = useRef(null);
  const carregarMovimentosComRetry = useCallback(async ({ force = false } = {}) => {
    const { scope, key } = cashScopeRef.current;
    // Escopo ainda indefinido (sessão/cadastro resolvendo no boot) → não mexe
    // nos movimentos nem invalida o pedido que estiver em curso.
    if (scope.modo === 'aguardando') return;
    if (!force && (movKeyCarregadaRef.current === key || movKeyEmCursoRef.current === key)) return;
    const { startStr, mvEnd } = janelaCaixaComanda();
    const seq = ++movReqRef.current;
    movKeyEmCursoRef.current = key;
    const MAX_TRIES = 3;
    try {
      for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
        try {
          const mv = await carregarMovimentos(scope, startStr, mvEnd);
          if (seq !== movReqRef.current) return; // um pedido mais novo assumiu
          setCashMovements(mv.rows);
          setCashMovementsAcesso(mv.acesso);
          setCashMovementsLoadError(null);
          movKeyCarregadaRef.current = key;
          return;
        } catch (err) {
          if (seq !== movReqRef.current) return;
          console.warn(`[cashMovements] tentativa ${attempt}/${MAX_TRIES} falhou:`, err?.message);
          if (attempt === MAX_TRIES) setCashMovementsLoadError(err?.message || 'Falha ao carregar os lançamentos');
          else await new Promise(r => setTimeout(r, attempt * 2500));
        }
      }
    } finally {
      if (seq === movReqRef.current) movKeyEmCursoRef.current = null;
    }
  }, []);

  const refreshCaixaComanda = useCallback(async () => {
    const { startStr, endStr } = janelaCaixaComanda();
    // Movimentos em paralelo, por conta própria (ver acima).
    const movimentos = carregarMovimentosComRetry({ force: true });
    // Comandas: mesmo escopo dos clientes (16/10/2026). Fora dele, só os caixas.
    const escopo = listaEscopoRef.current;
    const pedeComandas = escopo === 'sim';
    const MAX_TRIES = 3;
    for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
      try {
        const [cxs, cms] = await Promise.all([
          listCaixas({ startDate: startStr, endDate: endStr }),
          pedeComandas ? listComandas({ startDate: startStr, endDate: endStr }) : Promise.resolve(null),
        ]);
        setCaixas(cxs);
        // Escopo de AGORA (pode ter mudado durante o pedido: logout/outra conta)
        const escopoAgora = listaEscopoRef.current;
        if (cms && escopoAgora === 'sim') setComandas(cms);
        else if (limparLista(escopoAgora)) setComandas([]);
        setCaixaLoadError(null);
        break;
      } catch (err) {
        console.warn(`[caixaComanda] tentativa ${attempt}/${MAX_TRIES} falhou:`, err?.message);
        if (attempt === MAX_TRIES) {
          setCaixaLoadError(err?.message || 'Falha ao carregar dados');
        } else {
          await new Promise(r => setTimeout(r, attempt * 2500));
        }
      }
    }
    setCaixasLoaded(true);
    await movimentos;
  }, [carregarMovimentosComRetry]);

  // Boot / login / troca de conta (antes: só no mount, antes de a sessão
  // existir). Sem sessão não pede nada (o servidor daria 401) e limpa as
  // comandas; sessão/cadastro resolvendo → espera.
  useEffect(() => {
    const escopo = listaEscopoRef.current;
    if (escopo === 'aguardando') return;
    if (escopo === 'nenhum') { setComandas([]); return; }
    refreshCaixaComanda();
  }, [listaKey, refreshCaixaComanda]);

  // Escopo de movimentos mudou (sessão resolveu no boot, login, cadastro
  // carregou) → recarrega SÓ os movimentos (caixas/comandas não dependem do
  // cargo). Mesma chave já carregada (ou em curso) → não pede de novo.
  const refreshCashMovements = useCallback(() => carregarMovimentosComRetry(), [carregarMovimentosComRetry]);
  // 07/10/2026: recarga FORÇADA só dos movimentos — para telas que gravam por
  // fora do saveCashMovement (Calendário → AccountModal: editar/cancelar conta)
  // não deixarem o Contas a Pagar somando um título já cancelado/alterado.
  const recarregarCashMovements = useCallback(() => carregarMovimentosComRetry({ force: true }), [carregarMovimentosComRetry]);

  useEffect(() => { refreshCashMovements(); }, [cashScopeKey, refreshCashMovements]);

  // Rede voltou → recarrega sozinho (PWA no balcão fica offline com frequência)
  useEffect(() => {
    const onOnline = () => refreshCaixaComanda();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refreshCaixaComanda]);

  const saveCaixaRecord = useCallback(async (record) => {
    try {
      const saved = await upsertCaixaCloud(record);
      setCaixas(prev => {
        const idx = prev.findIndex(c => c.id === saved.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
        return [saved, ...prev];
      });
      return saved;
    } catch (err) {
      console.error('[saveCaixaRecord] payload:', record, 'err:', err);
      alert(`Erro ao salvar caixa:\n${err.message}`);
      throw err;
    }
  }, []);

  // Exclusão DEFINITIVA — só pro operador financeiro corrigir caixa aberto
  // errado (o /api/db recusa os demais). Comandas ficam sem o vínculo (ON
  // DELETE SET NULL); as sangrias/suprimentos (cash_movements) são APAGADAS
  // junto — ON DELETE CASCADE em migrations/caixas-comandas.sql (06/10/2026).
  const deleteCaixaRecord = useCallback(async (caixaId) => {
    try {
      await deleteCaixaCloud(caixaId);
      setCaixas(prev => prev.filter(c => c.id !== caixaId));
      // A cascata mexeu nas sangrias/suprimentos do caixa — relê do servidor
      // (é ele quem sabe o que sobrou).
      carregarMovimentosComRetry({ force: true });
      return true;
    } catch (err) {
      console.error('[deleteCaixaRecord]', err);
      alert(`Erro ao excluir caixa:\n${err.message}`);
      throw err;
    }
  }, [carregarMovimentosComRetry]);

  const saveComanda = useCallback(async (record) => {
    // Defensivo: se caixaId aponta pra caixa que NÃO existe mais (foi
    // apagado), seta pra null pra não dar 23503 FK violation. Cache local
    // pode ter referência velha.
    let safeRecord = record;
    if (record?.caixaId && !(caixas || []).find(c => c.id === record.caixaId)) {
      console.warn('[saveComanda] caixa órfão — limpando caixaId:', record.caixaId);
      safeRecord = { ...record, caixaId: null };
    }
    try {
      const saved = await upsertComandaCloud(safeRecord);
      setComandas(prev => {
        const idx = prev.findIndex(c => c.id === saved.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
        return [saved, ...prev];
      });
      return saved;
    } catch (err) {
      console.error('[saveComanda] payload:', safeRecord, 'err:', err);
      alert(`Erro ao salvar comanda:\n${err.message}`);
      throw err;
    }
  }, [caixas]);

  const saveCashMovement = useCallback(async (record) => {
    // Mesma defesa: caixaId órfão vira null pra não quebrar FK.
    let safeRecord = record;
    if (record?.caixaId && !(caixas || []).find(c => c.id === record.caixaId)) {
      console.warn('[saveCashMovement] caixa órfão — limpando caixaId:', record.caixaId);
      safeRecord = { ...record, caixaId: null };
    }
    try {
      const saved = await upsertCashMovementCloud(safeRecord);
      setCashMovements(prev => {
        const idx = prev.findIndex(m => m.id === saved.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
        return [saved, ...prev];
      });
      return saved;
    } catch (err) {
      console.error('[saveCashMovement] payload:', safeRecord, 'err:', err);
      alert(`Erro ao salvar movimentação:\n${err.message}`);
      throw err;
    }
  }, [caixas]);

  // 22/10/2026 (lapidação do financeiro, etapa B): removeCashMovement saiu
  // (nenhuma tela usava). Lançamento não se apaga: cancela com motivo.

  // 22/10/2026 (lapidação do financeiro, etapa E, FIN_EDICAO_SEGURA): a edição
  // segura grava por fora do saveCashMovement (PATCH com a versão lida,
  // utils/finEdicaoTituloApi.js) e também relê a conta ao abrir; a lista troca
  // só essa linha (sem recarregar tudo). Conta que não está na lista: nada muda.
  const trocarCashMovementLocal = useCallback((mov) => {
    if (!mov || !mov.id) return;
    setCashMovements(prev => {
      const idx = prev.findIndex(m => m.id === mov.id);
      if (idx < 0) return prev;
      const next = prev.slice(); next[idx] = mov; return next;
    });
  }, []);

  // ─── Notas Fiscais (LAZY) ──────────────────────────────────────────────
  // Janela reduzida pra 90 dias (era 6 meses) — quem precisa de histórico
  // longo abre a tela e filtra manualmente. Não carrega no boot.
  const refreshNotasFiscais = useCallback(async () => {
    try {
      const today = new Date();
      const start = new Date(today); start.setDate(today.getDate() - 90);
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const rows = await listNotasFiscais({ startDate: fmt(start), endDate: fmt(today) });
      setNotasFiscais(rows);
    } catch (err) {
      console.warn('[notasFiscais] load failed:', err.message);
    } finally {
      setNotasFiscaisLoaded(true);
    }
  }, []);

  const ensureNotasFiscaisLoaded = useCallback(() => {
    if (notasFiscaisLoaded) return;
    refreshNotasFiscais();
  }, [notasFiscaisLoaded, refreshNotasFiscais]);

  const saveNotaFiscal = useCallback(async (record) => {
    const saved = await upsertNotaFiscalCloud(record);
    setNotasFiscais(prev => {
      const idx = prev.findIndex(n => n.id === saved.id);
      if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
      return [saved, ...prev];
    });
    return saved;
  }, []);

  const removeNotaFiscal = useCallback(async (id) => {
    await deleteNotaFiscalCloud(id);
    setNotasFiscais(prev => prev.filter(n => n.id !== id));
  }, []);

  // ─── Estoque (produtos, fornecedores, mov, pedidos, inventário) ─────
  // products + suppliers carregam SEMPRE (comanda precisa pros itens tipo
  // 'product'). Movimentações/pedidos/inventário só quando entra em /estoque.
  const refreshEstoque = useCallback(async () => {
    try {
      const [pr, su, sm, po, ic] = await Promise.all([
        listProducts(), listSuppliers(),
        listStockMovements({ limit: 2000 }),
        listPurchaseOrders(),
        listInventoryCounts(),
      ]);
      setProducts(pr); setSuppliers(su); setStockMovements(sm);
      setPurchaseOrders(po); setInventoryCounts(ic);
    } catch (err) {
      console.warn('[estoque] load failed:', err.message);
    } finally {
      setEstoqueLoaded(true);
    }
  }, []);

  // Lite: só products + suppliers (precisam pro select de comanda).
  // Stock movements/pedidos/inventário ficam vazios até refreshEstoque rodar.
  const refreshEstoqueLite = useCallback(async () => {
    try {
      const [pr, su] = await Promise.all([listProducts(), listSuppliers()]);
      setProducts(pr); setSuppliers(su);
    } catch (err) {
      console.warn('[estoqueLite] load failed:', err.message);
    } finally {
      setEstoqueLoaded(true);
    }
  }, []);

  useEffect(() => { refreshEstoqueLite(); }, [refreshEstoqueLite]);

  const ensureEstoqueFullLoaded = useCallback(() => {
    // Já tem stockMovements/etc? Não recarrega.
    if (stockMovements.length > 0 || purchaseOrders.length > 0 || inventoryCounts.length > 0) return;
    refreshEstoque();
  }, [stockMovements.length, purchaseOrders.length, inventoryCounts.length, refreshEstoque]);

  const upsertLocal = (setter, saved) => setter(prev => {
    const idx = prev.findIndex(x => x.id === saved.id);
    if (idx >= 0) { const next = prev.slice(); next[idx] = saved; return next; }
    return [saved, ...prev];
  });

  const saveProduct = useCallback(async (p) => {
    const saved = await upsertProductCloud(p);
    upsertLocal(setProducts, saved);
    return saved;
  }, []);

  const removeProduct = useCallback(async (id) => {
    await deleteProductCloud(id);
    setProducts(prev => prev.filter(p => p.id !== id));
  }, []);

  const saveSupplierRecord = useCallback(async (s) => {
    const saved = await upsertSupplierCloud(s);
    upsertLocal(setSuppliers, saved);
    return saved;
  }, []);

  const saveStockMovement = useCallback(async (m) => {
    const saved = await upsertStockMovementCloud(m);
    upsertLocal(setStockMovements, saved);
    // Atualiza estoque do produto: ENTRADA soma, SAÍDA subtrai, AJUSTE define
    if (m.productId) {
      const product = products.find(p => p.id === m.productId);
      if (product) {
        let newStock = product.currentStock || 0;
        if (m.type === 'entrada') newStock += Number(m.quantity) || 0;
        else if (m.type === 'saida') newStock -= Number(m.quantity) || 0;
        else if (m.type === 'ajuste') newStock = Number(m.quantity) || 0;
        await saveProduct({ ...product, currentStock: newStock });
      }
    }
    return saved;
  }, [products, saveProduct]);

  const savePurchaseOrder = useCallback(async (p) => {
    const saved = await upsertPurchaseOrderCloud(p);
    upsertLocal(setPurchaseOrders, saved);
    return saved;
  }, []);

  const saveInventoryCount = useCallback(async (c) => {
    const saved = await upsertInventoryCountCloud(c);
    upsertLocal(setInventoryCounts, saved);
    return saved;
  }, []);

  // ─── Pacotes: créditos por cliente (Fase 5) ────────────────────────────
  // LAZY LOAD: não carrega no boot (custa egress). Carrega na primeira vez
  // que alguém chama `ensurePackageCreditsLoaded()` — tipicamente quando
  // a comanda modal abre. Pra ranking/agenda que não usa, fica vazio.
  const refreshPackageCredits = useCallback(async () => {
    try {
      const list = await listPackageCredits({});
      setPackageCredits(list);
    } catch (err) {
      console.warn('[packageCredits] load failed:', err.message);
    } finally {
      setPackageCreditsLoaded(true);
    }
  }, []);

  const ensurePackageCreditsLoaded = useCallback(() => {
    if (packageCreditsLoaded) return;
    refreshPackageCredits();
  }, [packageCreditsLoaded, refreshPackageCredits]);

  // NOTA: removido useEffect de boot — agora é lazy via ensurePackageCreditsLoaded.

  const savePackageCredit = useCallback(async (c) => {
    const saved = await upsertPackageCreditCloud(c);
    upsertLocal(setPackageCredits, saved);
    return saved;
  }, []);

  // Decrementa qty_remaining de um crédito; atualiza state local.
  const consumePackageCredit = useCallback(async (id, qty = 1) => {
    const saved = await consumePackageCreditCloud(id, qty);
    upsertLocal(setPackageCredits, saved);
    return saved;
  }, []);

  // INVERSO: estorna qty pra crédito (quando comanda reaberta/cancelada).
  const refundPackageCredit = useCallback(async (id, qty = 1) => {
    const saved = await refundPackageCreditCloud(id, qty);
    upsertLocal(setPackageCredits, saved);
    return saved;
  }, []);

  // Remove (deleta) TODOS os créditos criados por uma comanda. Usado quando a
  // venda do pacote é desfeita (cancela/reabre). Deleta de verdade pra não
  // deixar créditos fantasma inflando o saldo do cliente nem atrapalhando a
  // idempotência do grant no refinalizar (ver comentário em packageCreditsStorage).
  const deactivateCreditsBySource = useCallback(async (comandaId) => {
    const removed = await deactivateCreditsBySourceCloud(comandaId);
    // Atualiza state local: REMOVE os créditos deletados.
    const removedIds = new Set((removed || []).map(c => c.id));
    setPackageCredits(prev => prev.filter(c => !removedIds.has(c.id)));
    return removed;
  }, []);

  // Cria N créditos a partir da venda de UM pacote (1 linha por item).
  // Chamado em saveComanda quando uma comanda vira 'finalizada' e tem itens
  // tipo 'package'.
  // IDEMPOTENTE: se já existem créditos pra essa (comanda, pacote, cliente),
  // não cria de novo — protege contra duplo clique / re-finalização da
  // comanda que duplicava o saldo do cliente ("2/10" em vez de "1/5").
  const grantPackageCreditsFromSale = useCallback(async ({ pkg, clientId, comandaId, unitId, soldAt }) => {
    const rows = buildCreditsFromPackageSale({ pkg, clientId, comandaId, unitId, soldAt });
    if (rows.length === 0) return [];
    // Idempotência: checa se já tem credit ATIVO dessa comanda+pkg+cliente.
    // Só considera ativos — um crédito desativado/fantasma de um ciclo anterior
    // NÃO deve bloquear a criação de créditos frescos ao refinalizar.
    const dupes = (packageCredits || []).filter(c =>
      c.sourceComandaId === comandaId &&
      c.packageId === pkg.id &&
      c.clientId === clientId &&
      c.active !== false
    );
    if (dupes.length > 0) {
      console.info('[grantPackageCredits] já existe — pulando criação. dupes:', dupes.length);
      return dupes;
    }
    try {
      const created = await createCreditsBatch(rows);
      setPackageCredits(prev => [...created, ...prev]);
      return created;
    } catch (err) {
      console.error('[grantPackageCredits] erro:', err);
      alert(`Falha ao criar créditos do pacote:\n${err.message}`);
      throw err;
    }
  }, [packageCredits]);

  // Filtra tombstones — items "deletados" continuam no array localStorage/cloud
  // (pra sync respeitar o delete e não ressuscitar via cache antigo de outro
  // device), mas a UI não vê. Aplicado em TODAS as chaves com tombstone.
  const visiblePackages = useMemo(
    () => (packages || []).filter(p => !p?.deletedAt),
    [packages]
  );
  const visibleHolidays = useMemo(
    () => (holidays || []).filter(h => !h?.deletedAt),
    [holidays]
  );
  const visibleUnits = useMemo(
    () => (units || []).filter(u => !u?.deletedAt),
    [units]
  );
  const visibleProfessionals = useMemo(
    () => (professionals || []).filter(p => !p?.deletedAt),
    [professionals]
  );
  const visibleProfChallenges = useMemo(
    () => (profChallenges || []).filter(c => !c?.deletedAt),
    [profChallenges]
  );

  // 27/10/2026 (CRM_SEM_RECARGA): o value memoizado (os callbacks já são useCallback); sem o
  // interruptor, um objeto novo a cada render como antes
  const valorMemo = useMemo(() => ({
      users, performance, unitPerformance,
      // Listas "vivas" pra UI — sem tombstones.
      services,
      units: visibleUnits,
      professionals: visibleProfessionals,
      packages: visiblePackages,
      holidays: visibleHolidays,
      profChallenges: visibleProfChallenges,
      // Listas "raw" incluindo tombstones — pra lookup por id em históricos
      // (ex: agendamento antigo referencia unit que foi deletada — precisa
      // achar o nome dela). Use prefixo `_all` pra deixar claro que é raw.
      _allUnits: units,
      _allProfessionals: professionals,
      _allPackages: packages,
      dailyRevenue, unitMetas, unitConfig, weeklyDuels, cloudLoaded, cloudReloadTick,
      clients, clientsLoaded,
      appointments, appointmentsLoaded, refreshAppointments,
      saveAppointmentRecord, removeAppointment,
      caixas, comandas, cashMovements, cashMovementsAcesso, cashMovementsLoadError, caixasLoaded, refreshCaixaComanda, caixaLoadError,
      recarregarCashMovements,
      saveCaixaRecord, deleteCaixaRecord, saveComanda, saveCashMovement, trocarCashMovementLocal,
      notasFiscais, notasFiscaisLoaded, refreshNotasFiscais, ensureNotasFiscaisLoaded,
      saveNotaFiscal, removeNotaFiscal,
      products, suppliers, stockMovements, purchaseOrders, inventoryCounts,
      estoqueLoaded, refreshEstoque, ensureEstoqueFullLoaded,
      saveProduct, removeProduct, saveSupplierRecord, saveStockMovement,
      savePurchaseOrder, saveInventoryCount,
      // Pacotes — créditos por cliente (cross-comanda). LAZY: chamar
      // ensurePackageCreditsLoaded() na primeira vez que precisar (não vem
      // no boot pra economizar egress do Supabase).
      packageCredits, packageCreditsLoaded, refreshPackageCredits, ensurePackageCreditsLoaded,
      savePackageCredit, consumePackageCredit, grantPackageCreditsFromSale,
      refundPackageCredit, deactivateCreditsBySource,
      refreshAll, refreshFromCloud, addUnit, updateUnit, deleteUnit, addProfessional, updateProfessional, deleteProfessional,
      addUser, updateUser, savePerf, saveUnitPerf, getProfessionalPerf, getUnitPerf,
      getUnitPerfRecord, getPeriodPerf, addService, updateService, deleteService, addPackage, updatePackage, deletePackage,
      saveDailyRevenueRecord, saveUnitMetaRecord, saveUnitConfigRecord, saveHolidaysList,
      bloquearDiaFechado, desbloquearDiaFechado,
      saveProfChallenge, removeProfChallenge, saveWeeklyDuel,
      refreshClients, saveClient, removeClient, mergeClients,
  }), [ // eslint-disable-line react-hooks/exhaustive-deps
    users, performance, unitPerformance, services, visibleUnits, visibleProfessionals, visiblePackages, visibleHolidays, visibleProfChallenges, units, professionals, packages, dailyRevenue, unitMetas, unitConfig, weeklyDuels, cloudLoaded, cloudReloadTick, clients, clientsLoaded, appointments, appointmentsLoaded, refreshAppointments, saveAppointmentRecord, removeAppointment, caixas, comandas, cashMovements, cashMovementsAcesso, cashMovementsLoadError, caixasLoaded, refreshCaixaComanda, caixaLoadError, recarregarCashMovements, saveCaixaRecord, deleteCaixaRecord, saveComanda, saveCashMovement, trocarCashMovementLocal, notasFiscais, notasFiscaisLoaded, refreshNotasFiscais, ensureNotasFiscaisLoaded, saveNotaFiscal, removeNotaFiscal, products, suppliers, stockMovements, purchaseOrders, inventoryCounts, estoqueLoaded, refreshEstoque, ensureEstoqueFullLoaded, saveProduct, removeProduct, saveSupplierRecord, saveStockMovement, savePurchaseOrder, saveInventoryCount, packageCredits, packageCreditsLoaded, refreshPackageCredits, ensurePackageCreditsLoaded, savePackageCredit, consumePackageCredit, grantPackageCreditsFromSale, refundPackageCredit, deactivateCreditsBySource, refreshAll, refreshFromCloud, addUnit, updateUnit, deleteUnit, addProfessional, updateProfessional, deleteProfessional, addUser, updateUser, savePerf, saveUnitPerf, getProfessionalPerf, getUnitPerf, getUnitPerfRecord, getPeriodPerf, addService, updateService, deleteService, addPackage, updatePackage, deletePackage, saveDailyRevenueRecord, saveUnitMetaRecord, saveUnitConfigRecord, saveHolidaysList, bloquearDiaFechado, desbloquearDiaFechado, saveProfChallenge, removeProfChallenge, saveWeeklyDuel, refreshClients, saveClient, removeClient, mergeClients,
  ]);
  const valor = CRM_SEM_RECARGA ? valorMemo : { ...valorMemo };

  return (
    <DataContext.Provider value={valor}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
